import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import AsyncButton from "@/components/AsyncButton";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUnsavedChangesWarning } from "@/hooks/useUnsavedChangesWarning";
import { CommentItem } from "../../context/AgreementContext";
import {
  addComment,
  removeComment,
  updateComment,
  getValidComment,
  initializeComments,
} from "../../utils";

type DialogType = "unsavedChanges";

const DIALOG_TRANSLATIONS = {
  unsavedChanges: {
    titleKey: "agreements.comments.unsavedChanges.title",
    descriptionKey: "agreements.comments.unsavedChanges.description",
    confirmKey: "common.discard",
  },
} as const;

interface AgreementCommentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (comments: CommentItem[]) => Promise<unknown>;
  isPending?: boolean;
  initialComments?: CommentItem[];
}

export const AgreementCommentDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  initialComments = [],
}: AgreementCommentDialogProps) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [activeDialog, setActiveDialog] = useState<DialogType | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (open) {
      setComments(initializeComments(initialComments));
      setIsDirty(false);
    }
  }, [open, initialComments]);

  const handleAddComment = useCallback(() => {
    setComments((prev) => addComment(prev));
  }, []);

  const handleRemoveComment = useCallback((id: string) => {
    setComments((prev) => removeComment(prev, id));
  }, []);

  const handleCommentChange = useCallback((id: string, value: string) => {
    setComments((prev) => updateComment(prev, id, value));
    setIsDirty(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    const validComments = getValidComment(comments);
    if (validComments.length > 0) {
      await onConfirm(validComments);
      setIsDirty(false);
      onOpenChange(false);
    }
  }, [comments, onConfirm, onOpenChange]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && isDirty) {
        setActiveDialog("unsavedChanges");
      } else if (!isOpen) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isDirty],
  );

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setActiveDialog("unsavedChanges");
    } else {
      onOpenChange(false);
    }
  }, [onOpenChange, isDirty]);

  const handleForceClose = useCallback(() => {
    setActiveDialog(null);
    setIsDirty(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const dialogTranslations = useMemo(
    () => (activeDialog ? DIALOG_TRANSLATIONS[activeDialog] : null),
    [activeDialog],
  );

  const handleDialogConfirm = useCallback(() => {
    if (activeDialog === "unsavedChanges") {
      handleForceClose();
    }
  }, [activeDialog, handleForceClose]);

  const canSubmit = getValidComment(comments).length > 0;

  useUnsavedChangesWarning(open && isDirty);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t("agreements.comments.submitWarning.title")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                  {t("agreements.comments.title")}
                </label>
                <Button
                  onClick={handleAddComment}
                  size="sm"
                  variant="ghost"
                  disabled={isPending}
                  className="h-8 w-8 p-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-2 items-start">
                  <Textarea
                    value={comment.text}
                    onChange={(e) =>
                      handleCommentChange(comment.id, e.target.value)
                    }
                    placeholder={t("agreements.comments.addPlaceholder")}
                    rows={2}
                    className="flex-1"
                    disabled={isPending}
                  />
                  {comments.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveComment(comment.id)}
                      className="text-gray-500 hover:text-red-600 hover:bg-red-50 mt-1"
                      disabled={isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isPending}
            >
              {t("common.cancel")}
            </Button>
            <AsyncButton
              onClick={handleConfirm}
              disabled={!canSubmit}
              loading={isPending}
              label={t("common.confirm")}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dialogTranslations && (
        <AlertDialog
          open={!!activeDialog}
          onOpenChange={(open) => !open && setActiveDialog(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(dialogTranslations.titleKey)}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(dialogTranslations.descriptionKey)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setActiveDialog(null)}>
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDialogConfirm}>
                {t(dialogTranslations.confirmKey)}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};
